import { useState, useEffect, useCallback } from "react";
import { hoResolveDeal } from "../../lib/homeownerRpc";
import type { PickedLine } from "../../lib/scopeLines";

export interface HomeownerDeal {
  id: string;
  homeowner_name: string;
  homeowner_phone: string;
  homeowner_email: string | null;
  property_street: string;
  property_city: string | null;
  property_state: string | null;
  property_zip: string | null;
  stage: string;
  phase: string;
  owner_id: string | null;
  owner_name?: string;
  acv_amount: number | null;
  deductible: number | null;
  total_contract_amount: number | null;
  initial_invoice_amount: number | null;
  pre_build_amount_received: number | null;
  post_build_amount_received: number | null;
  // Payments handed over but not yet bank-cleared (checks in the iink
  // pipeline, Stripe at 'made'). Jun 10, 2026 — homeowners couldn't see
  // money they'd already paid because *_received only counts cleared.
  pre_build_amount_in_flight: number | null;
  post_build_amount_in_flight: number | null;
  post_build_balance_remaining: number | null;
  // Residual Shannon wrote off (May 21, 2026) — not owed, subtracted on screen.
  balance_written_off: number | null;
  build_scheduled_date: string | null;
  /** Last day of a build that runs more than one day (null = one day). */
  build_end_date?: string | null;
  build_completed_date: string | null;
  shingle_color: string | null;
  drip_edge_color: string | null;
  claim_number: string | null;
  insurance_company: string | null;
  // Documents
  scope_document_url: string | null;
  install_agreement_pdf_url: string | null;
  install_agreement_signed: boolean | null;
  install_agreement_signed_date: string | null;
  color_addendum_url: string | null;
  color_addendum_signed: boolean | null;
  // Set by ho_sign_agreement when a color was left open at signing; drives
  // the portal's "Choose / Confirm your colors" banner (Oct 22, 2026).
  color_addendum_required: boolean | null;
  material_order_date: string | null;
  // Scope-calc fields (Apr 30, 2026): exposed to HO portal so the live
  // Payment Schedule card on the Payment tab can compute the same numbers
  // the install agreement PDF shows. supplements + supplement_approved_amount
  // flow into the After Build card automatically as they're approved.
  rcv_total: number | null;
  depreciation_total: number | null;
  supplements: number | null;
  supplement_invoice_amount: number | null;
  pwi_code: number | null;
  upgrades: number | null;
  retail_profit: number | null;
  at_cost_retail: number | null;
  upgrades_description?: string | null;
  retail_profit_description?: string | null;
  at_cost_retail_description?: string | null;
  misc_credit: number | null;
  non_commissionable: number | null;
  acv_only: boolean | null;
  // Retail option (Jun 11, 2026) — 50% deposit / 50% on completion. The
  // scope-calc retail branch reads these to override the payment schedule.
  is_retail: boolean | null;
  retail_contract_total: number | null;
  retail_squares: number | null;
  retail_scope_description: string | null;
  retail_base_per_square: number | null;
  living_invoice_url: string | null;
  living_invoice_token: string | null;
  // Supplement tracking
  supplement_needed: boolean | null;
  supplement_status: string | null;
  supplement_submitted_to_insurance_date: string | null;
  supplement_approved_amount: number | null;
  supplement_approved_date: string | null;
  // Invoice tracking
  initial_invoice_pdf_sent: boolean | null;
  transparency_invoice_pdf_sent: boolean | null;
  final_invoice_pdf_sent: boolean | null;
  final_invoice_amount: number | null;
  updated_invoice_amount: number | null;
  // Warranty
  warranty_status: string | null;
  warranty_type: string | null;
  oc_warranty_number: string | null;
  warranty_registered_date: string | null;
  warranty_expiration_date: string | null;
  // Agreement data (JSONB from generate-agreement edge function)
  agreement_data: AgreementData | null;
  agreement_status: string | null;
  signing_link: string | null;
  agreement_signing_url: string | null;
  // Sharing flags — controls what shows on portal
  drone_report_shared_with_ho: boolean | null;
  install_agreement_shared_with_ho: boolean | null;
  imging_report_link: string | null;
  // Un-performed line items (depreciation release photos)
  items_not_performing_lines: string | null;
  /** Oct 30, 2026: the carrier lines Bardin won't do, one per line (ho_resolve_deal keeps them; it strips scope_gate). */
  items_not_performing_detail?: PickedLine[] | null;
  acv_credit: number | null;
  dep_credit: number | null;
  unperf_completion_uploaded_at: string | null;
}

/** Shape of the agreement_data JSONB column populated by generate-agreement */
export interface AgreementLineItem {
  line_number: number;
  description: string;
  category: string;
  rcv: number;
  acv: number;
  depreciation: number;
}

export interface AgreementRetailAddon {
  description: string;
  amount: number;
}

export interface AgreementFinancials {
  rcv_total: number;
  acv_total: number;
  depreciation_total: number;
  recoverable_depreciation: number;
  non_recoverable_depreciation: number;
  deductible: number;
  pwi_code_amount: number;
  excluded_acv_total: number;
  excluded_rcv_total: number;
  retail_addons_total: number;
  misc_credit: number;
  net_deductible: number;
  out_of_pocket: number;
  initial_invoice_amount: number;
}

export interface AgreementData {
  scope_items: AgreementLineItem[];
  excluded_items: AgreementLineItem[];
  retail_addons: AgreementRetailAddon[];
  financials: AgreementFinancials;
  misc_credit: number;
  product_line: string;
  generated_at: string;
}

/**
 * Token-based auth for homeowner pages.
 * URL has ?token=<base64(dealId:lastName:streetNum)>
 * Or we prompt for last name + street number and match against the deal.
 */
export function useHomeownerAuth() {
  const [deal, setDeal] = useState<HomeownerDeal | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [needsAuth, setNeedsAuth] = useState(false);

  // Parse token from URL
  const parseToken = useCallback(() => {
    const params = new URLSearchParams(window.location.search);
    const token = params.get("token");
    if (!token) return null;
    try {
      const decoded = atob(token);
      const [dealId, lastName, streetNum] = decoded.split(":");
      if (!dealId || !lastName || !streetNum) return null;
      return { dealId, lastName: lastName.toLowerCase(), streetNum };
    } catch {
      return null;
    }
  }, []);

  // Look up deal by token
  const lookupDeal = useCallback(
    async (dealId: string, lastName: string, streetNum: string) => {
      // Token-validated server-side lookup. ho_resolve_deal checks last name +
      // street number in SQL and returns the deal row (+owner_name) or null —
      // so the portal never touches the deals/team_roster tables directly.
      const { data, error: fetchErr } = await hoResolveDeal(dealId, lastName, streetNum);

      if (fetchErr || !data) {
        setError("The information you entered doesn't match our records.");
        setLoading(false);
        return;
      }

      setDeal(data as HomeownerDeal);
      setLoading(false);
    },
    []
  );

  // Manual auth (last name + street number form)
  const authenticate = useCallback(
    async (dealId: string, lastName: string, streetNum: string) => {
      setLoading(true);
      setError(null);
      await lookupDeal(dealId, lastName, streetNum);
    },
    [lookupDeal]
  );

  // On mount, try token
  useEffect(() => {
    const token = parseToken();
    if (token) {
      lookupDeal(token.dealId, token.lastName, token.streetNum);
    } else {
      // Check if dealId is in path  e.g. /status/abc-123
      const pathParts = window.location.pathname.split("/");
      const possibleDealId = pathParts[pathParts.length - 1];
      if (
        possibleDealId &&
        possibleDealId.match(
          /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/
        )
      ) {
        setNeedsAuth(true);
        setLoading(false);
      } else {
        setError("Invalid link. Please use the link sent to you by text.");
        setLoading(false);
      }
    }
  }, [parseToken, lookupDeal]);

  return { deal, loading, error, needsAuth, authenticate };
}
